import type { Account, Category, ParseFlag, TransactionDraft, TransactionKind } from '../types'

export interface ParserContext {
  today: string
  accounts: Account[]
  categories: Category[]
  defaultAccountId?: string | null
}

const amountPattern = /(?<![\w])(?:rp\s*)?(\d+(?:[.,]\d+)*)(?:\s*)(jt|juta|rb|ribu|k)?(?![\w])/gi
const transferWords = /\b(transfer|pindah(?:kan)?|kirim)\b/i
const incomeWords = /\b(gaji|bonus|freelance|honor|pendapatan|masuk|dibayar|terima)\b/i

function addDays(iso: string, days: number): string {
  const [year, month, day] = iso.split('-').map(Number)
  const date = new Date(Date.UTC(year, month - 1, day + days))
  return date.toISOString().slice(0, 10)
}

function parseAmount(rawNumber: string, suffix?: string): bigint | null {
  const normalizedSuffix = suffix?.toLowerCase()
  const compact = rawNumber.trim()

  if (normalizedSuffix === 'jt' || normalizedSuffix === 'juta') {
    const decimal = compact.replace(',', '.')
    if (!/^\d+(?:\.\d{1,3})?$/.test(decimal)) return null
    const [whole, fraction = ''] = decimal.split('.')
    return BigInt(whole) * 1_000_000n + BigInt(fraction.padEnd(3, '0')) * 1_000n
  }

  if (normalizedSuffix === 'rb' || normalizedSuffix === 'ribu' || normalizedSuffix === 'k') {
    if (!/^\d+$/.test(compact)) return null
    return BigInt(compact) * 1_000n
  }

  if (/^\d{1,3}(?:\.\d{3})+$/.test(compact)) return BigInt(compact.replaceAll('.', ''))
  if (/^\d{1,3}(?:,\d{3})+$/.test(compact)) return BigInt(compact.replaceAll(',', ''))
  if (/^\d+$/.test(compact)) return BigInt(compact)
  return null
}

function normalize(value: string): string {
  return value.toLocaleLowerCase('id-ID').replace(/[^a-z0-9]+/g, ' ').trim()
}

function findAccount(text: string, accounts: Account[]): Account | undefined {
  const haystack = ` ${normalize(text)} `
  return accounts
    .filter((account) => !account.archived_at)
    .sort((a, b) => b.name.length - a.name.length)
    .find((account) => haystack.includes(` ${normalize(account.name)} `))
}

function findCategory(text: string, categories: Category[], kind: TransactionKind): Category | undefined {
  if (kind === 'transfer') return undefined
  const haystack = ` ${normalize(text)} `
  const expected = kind === 'income' ? 'income' : 'expense'
  return categories
    .filter((category) => category.kind === expected && !category.archived_at)
    .flatMap((category) => [category.name, ...category.aliases].map((term) => ({ category, term })))
    .sort((a, b) => b.term.length - a.term.length)
    .find(({ term }) => haystack.includes(` ${normalize(term)} `))?.category
}

export function parseTransaction(text: string, context: ParserContext): TransactionDraft {
  const flags: ParseFlag[] = []
  const amountMatches = [...text.matchAll(amountPattern)]
    .map((match) => ({ match, amount: parseAmount(match[1], match[2]) }))
    .filter((result) => result.amount !== null)

  if (amountMatches.length === 0) {
    flags.push({ code: 'amount_missing', message: 'Nominal belum ditemukan.', severity: 'error' })
  } else if (amountMatches.length > 1) {
    flags.push({ code: 'amount_ambiguous', message: 'Ada lebih dari satu nominal. Pilih nominal yang benar.', severity: 'error' })
  }

  const kind: TransactionKind = transferWords.test(text) ? 'transfer' : incomeWords.test(text) ? 'income' : 'expense'
  const transferParts = kind === 'transfer' ? normalize(text).match(/\bdari\s+(.+?)\s+ke\s+(.+)$/i) : null
  const transferSource = transferParts ? findAccount(transferParts[1], context.accounts) : undefined
  const transferDestination = transferParts ? findAccount(transferParts[2], context.accounts) : undefined
  const account = findAccount(text, context.accounts)
  const category = findCategory(text, context.categories, kind)

  if (!account && kind !== 'transfer') {
    flags.push({ code: 'account_missing', message: 'Dompet belum disebutkan. Pilih akun sebelum menyimpan.', severity: 'error' })
  }

  if (kind !== 'transfer' && !category) {
    flags.push({ code: 'category_missing', message: 'Kategori belum dikenali. Pilih sebelum menyimpan.', severity: 'warning' })
  }

  if (kind === 'transfer') {
    if (!transferSource) flags.push({ code: 'transfer_source', message: 'Pilih akun sumber transfer.', severity: 'warning' })
    if (!transferDestination) flags.push({ code: 'transfer_destination', message: 'Pilih akun tujuan transfer.', severity: 'warning' })
    if (transferSource?.id === transferDestination?.id) flags.push({ code: 'transfer_same_account', message: 'Akun sumber dan tujuan harus berbeda.', severity: 'error' })
  }

  const localDate = /\b(kemarin)\b/i.test(text) ? addDays(context.today, -1) : context.today
  const amount = amountMatches.length === 1 ? amountMatches[0].amount ?? 0n : 0n
  if (amount <= 0n && amountMatches.length > 0) {
    flags.push({ code: 'amount_non_positive', message: 'Nominal harus lebih dari Rp0.', severity: 'error' })
  }

  const strippedDescription = text
    .replace(amountPattern, ' ')
    .replace(/\b(hari ini|kemarin|dari|ke|masuk)\b/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  const hasError = flags.some((flag) => flag.severity === 'error')
  const needsReview = flags.some((flag) => flag.severity === 'warning')

  return {
    kind,
    amount_idr: amount.toString(),
    local_date: localDate,
    description: strippedDescription || (kind === 'transfer' ? 'Transfer akun' : 'Transaksi'),
    category_id: category?.id ?? null,
    category_name: category?.name,
    source_account_id: kind === 'income' ? null : kind === 'transfer' ? transferSource?.id ?? null : account?.id ?? null,
    destination_account_id: kind === 'income' ? account?.id ?? null : kind === 'transfer' ? transferDestination?.id ?? null : null,
    fee_idr: '0',
    flags,
    confidence: hasError ? 'invalid' : needsReview ? 'review' : 'high',
  }
}
